import React from 'react';
import PropTypes from 'prop-types';
import { Badge, EmptyState } from 'components/common';
import { LuCheckCircle } from 'react-icons/lu';

const formatDate = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

// "Crm::Project::Building::Adaptation" is too wide for a column and the tail is
// the part that identifies it; the full name stays in the tooltip.
const shortType = (recordType) => {
  const parts = String(recordType || '').split('::');
  return parts.slice(-2).join('::') || '—';
};

const OrphanTable = ({ rows, selectedIds, onToggle, onTogglePage, loading }) => {
  const pageIds     = rows.map((row) => row.id);
  const selectedAll = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  if (!loading && rows.length === 0) {
    return (
      <EmptyState
        icon={LuCheckCircle}
        title="No orphaned attachments"
        description="Every attachment on this page points at a blob that still exists."
      />
    );
  }

  return (
    <div className="tw-overflow-x-auto">
      <table className="tw-w-full tw-text-[12.5px] tw-border-collapse">
        <thead>
          <tr className="tw-text-left tw-text-slate-500 tw-border-b tw-border-slate-200">
            <th className="tw-py-2 tw-px-3 tw-w-8">
              <input
                type="checkbox"
                checked={selectedAll}
                onChange={() => onTogglePage(pageIds, !selectedAll)}
                aria-label="Select every row on this page"
                className="tw-cursor-pointer"
              />
            </th>
            <th className="tw-py-2 tw-px-3 tw-font-semibold">Attachment</th>
            <th className="tw-py-2 tw-px-3 tw-font-semibold">Belongs to</th>
            <th className="tw-py-2 tw-px-3 tw-font-semibold">Field</th>
            <th className="tw-py-2 tw-px-3 tw-font-semibold">Uploaded by</th>
            <th className="tw-py-2 tw-px-3 tw-font-semibold">Uploaded at</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const selected = selectedIds.has(row.id);
            return (
              <tr
                key={row.id}
                className={[
                  'tw-border-b tw-border-slate-100',
                  selected ? 'tw-bg-primary-50/60' : 'hover:tw-bg-slate-50',
                ].join(' ')}
              >
                <td className="tw-py-2 tw-px-3">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggle(row.id)}
                    aria-label={`Select attachment ${row.id}`}
                    className="tw-cursor-pointer"
                  />
                </td>
                <td className="tw-py-2 tw-px-3 tw-font-medium tw-text-slate-800 tw-tabular-nums">#{row.id}</td>
                <td className="tw-py-2 tw-px-3">
                  <span className="tw-text-slate-700" title={row.record_type}>
                    {shortType(row.record_type)}
                  </span>
                  <span className="tw-text-slate-400 tw-tabular-nums">#{row.record_id}</span>
                  {/* The parent being gone too removes any doubt about the row. */}
                  {row.record_exists === false && (
                    <Badge variant="warning" size="sm" className="tw-ml-2">parent gone</Badge>
                  )}
                  {row.record_exists === null && (
                    <Badge variant="default" size="sm" className="tw-ml-2">unknown type</Badge>
                  )}
                </td>
                <td className="tw-py-2 tw-px-3 tw-text-slate-600">{row.name}</td>
                <td className="tw-py-2 tw-px-3 tw-text-slate-600">{row.created_by || '—'}</td>
                <td className="tw-py-2 tw-px-3 tw-text-slate-500">{formatDate(row.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

OrphanTable.propTypes = {
  rows:         PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedIds:  PropTypes.instanceOf(Set).isRequired,
  onToggle:     PropTypes.func.isRequired,
  onTogglePage: PropTypes.func.isRequired,
  loading:      PropTypes.bool,
};

export default OrphanTable;
